import Pusher from 'pusher-js';

const PUSHER_KEY = import.meta.env.VITE_PUSHER_KEY ?? '';
const PUSHER_SECRET = import.meta.env.VITE_PUSHER_SECRET ?? '';
const PUSHER_CLUSTER = import.meta.env.VITE_PUSHER_CLUSTER ?? '';

export const PUSHER_CHANNEL_NAME = import.meta.env.VITE_PUSHER_CHANNEL || 'private-aigle-control';
export const PUSHER_PRESS_EVENT = 'client-press';
export const PUSHER_RESET_EVENT = 'client-reset';

export const isPusherConfigured = Boolean(PUSHER_KEY && PUSHER_SECRET && PUSHER_CLUSTER);

async function hmacSha256Hex(secret: string, message: string): Promise<string> {
  const encoder = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signatureBuffer = await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message));
  return Array.from(new Uint8Array(signatureBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

let pusherInstance: Pusher | null = null;

/**
 * The app has no backend, so private-channel auth (normally a server call)
 * is self-signed here in the browser with the same secret both pages share.
 * Requires "Client Events" enabled on the Pusher app and a secure context
 * (HTTPS or localhost) for crypto.subtle to be available.
 */
export function getPusherClient(): Pusher {
  if (pusherInstance) return pusherInstance;

  if (import.meta.env.DEV) {
    Pusher.logToConsole = true;
  }

  if (!window.isSecureContext) {
    console.error(
      '[pusher] not a secure context (needs HTTPS or localhost) — crypto.subtle is unavailable, '
      + 'so the private-channel auth signature cannot be computed.',
    );
  }

  pusherInstance = new Pusher(PUSHER_KEY, {
    cluster: PUSHER_CLUSTER,
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        hmacSha256Hex(PUSHER_SECRET, `${socketId}:${channel.name}`)
          .then((signature) => callback(null, { auth: `${PUSHER_KEY}:${signature}` }))
          .catch((error: unknown) => {
            console.error('[pusher] failed to sign channel auth', error);
            callback(error as Error, null);
          });
      },
    }),
  });

  pusherInstance.connection.bind('error', (error: unknown) => {
    console.error('[pusher] connection error', error);
  });

  return pusherInstance;
}
